import { Schema } from "firebase/vertexai";
import { model as baseModel } from "./firebase";

// Define schema for message extraction
const messageExtractionSchema = Schema.object({
  properties: {
    result: Schema.array({
      items: Schema.object({
        properties: {
          content: Schema.string(),
          side: Schema.string(),
        },
      }),
    }),
  },
});

/**
 * Determines the MIME type based on the base64 string
 * @param base64String The base64 string (without prefix)
 * @returns A string with the detected MIME type
 */
const getMimeTypeFromBase64 = (base64String: string): string => {
  // Check the first few characters to determine file type
  // Simplified version - in production this should be more robust
  const header = base64String.substring(0, 5);

  if (header.startsWith("/9j/")) {
    return "image/jpeg";
  } else if (header.startsWith("iVBOR")) {
    return "image/png";
  } else if (header.startsWith("R0lGO")) {
    return "image/gif";
  } else if (header.startsWith("UklGR")) {
    return "image/webp";
  } else if (header.startsWith("AAAAI")) {
    return "image/heic";
  }

  // Default to jpeg if we can't determine
  return "image/jpeg";
};

/**
 * Process a single image with Gemini
 * @param base64Image Base64-encoded image string
 * @returns Extracted text with information about position (left/right)
 */
const processImage = async (
  base64Image: string
): Promise<Array<{ content: string; side: string }>> => {
  try {
    // Create image part for the model with proper MIME type detection
    const imagePart = {
      inlineData: {
        data: base64Image,
        mimeType: getMimeTypeFromBase64(base64Image),
      },
    };

    // The prompt for Gemini to extract text from chat screenshots
    const prompt =
      "Extract and return all the text messages visible in these images. Keep any internal message formatting and emojis. Also return whether the message is on the left side of the image or the right side. Return them as a JSON array of objects with the following structure: {result: [{ content: `extracted text`, side: `left | right` }, ...]}. Sometimes the message will be a response to a previous message (which will appear in a different format to the rest of the messages), in which case you should ignore the referenced messaged and return only the content of the response and the side of the response. Sometimes messages will have reactions (usually in the form of an emoji) below or to the side of the message bubble, in which case you should ignore and not include it in the output. Don't add formatting like bold, italics, or new lines to the output, return only the text content and emojis.";

    // Use the model with schema configuration applied inline
    const result = await baseModel.generateContent({
      contents: [
        {
          role: "user",
          parts: [{ text: prompt }, { inlineData: imagePart.inlineData }],
        },
      ],
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: messageExtractionSchema,
      },
    });

    const response = result.response;
    const text = response.text();

    try {
      // Parse the response as JSON
      const formattedResponse = JSON.parse(text);

      // Check if we have result property or if it's a messages property we need to transform
      if (formattedResponse.result) {
        return formattedResponse.result;
      } else if (formattedResponse.messages) {
        // Transform the format if it returned messages instead of result
        return formattedResponse.messages.map(
          (msg: { content: string; side: string }) => ({
            content: msg.content,
            side: msg.side,
          })
        );
      }

      // If neither structure exists, return empty array
      return [];
    } catch (parseError) {
      console.error("Error parsing Gemini response:", parseError);

      // Try to extract JSON from text if direct parsing fails
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const extractedJson = jsonMatch[0];
          const parsedJson = JSON.parse(extractedJson);

          // Check for different response structures
          if (parsedJson.result) {
            return parsedJson.result;
          } else if (parsedJson.messages) {
            return parsedJson.messages.map(
              (msg: { content: string; side: string }) => ({
                content: msg.content,
                side: msg.side,
              })
            );
          }
        } catch (e) {
          console.error("Error parsing extracted JSON:", e);
        }
      }

      return [];
    }
  } catch (error) {
    console.error("Error calling Gemini model for single image:", error);
    return [];
  }
};

/**
 * Extracts text from images using Google Gemini
 * @param images Array of base64-encoded image strings
 * @param progressCallback Optional callback function to report progress
 * @returns Extracted text with information about position (left/right)
 */
export const extractTextFromImagesWithGemini = async (
  images: string[],
  progressCallback?: (index: number, totalExtracted: number) => void
): Promise<{ result: Array<{ content: string; side: string }> }> => {
  try {
    // Process each image individually and combine results
    const allResults: Array<{ content: string; side: string }> = [];

    // Process images sequentially to avoid overloading the API
    for (let i = 0; i < images.length; i++) {
      // Update progress to show which image we're starting to process
      if (progressCallback) {
        progressCallback(i + 1, allResults.length);
      }

      const results = await processImage(images[i]);
      allResults.push(...results);

      // Update progress after adding new results - this indicates image has been processed
      if (progressCallback && i < images.length - 1) {
        progressCallback(i + 2, allResults.length); // Report we're about to start the next image
      }
    }

    // Return combined results
    return { result: allResults };
  } catch (error) {
    console.error("Error processing images with Gemini:", error);

    // Fallback result if processing fails
    return {
      result: [
        {
          content:
            "Failed to extract messages from images. Please try again or upload clearer images.",
          side: "left",
        },
      ],
    };
  }
};
